import React, { useEffect, useState, useCallback } from 'react';
import { Music, Image as ImageIcon, Sparkles, FileText, Check, ChevronRight, ChevronLeft } from 'lucide-react';
import { useLang } from '../../context/LanguageContext';
import { API_BASE as API } from '../../lib/apiBase';

const COVER_HINT_ES =
  'Recomendado: imagen cuadrada min. 1400×1400 px (1:1) o 1920×1080 px (16:9). Si subes otra proporción, la enmarcamos automáticamente; puedes recortar antes para control total.';
const COVER_HINT_EN =
  'Recommended: square min. 1400×1400 px (1:1) or 1920×1080 px (16:9). Other ratios are smart-cropped; pre-crop for full control.';

/** Same order as backend `legal_markets.PRIORITY_MARKET_CODES` (10 markets). */
const PRIORITY_MARKET_CODES = ['ES', 'DE', 'FR', 'IT', 'PT', 'GB', 'US', 'MX', 'CO', 'BR'];

const emptyLegal = () => ({
  jurisdiction_country: '',
  master_rights: 'self',
  publishing_rights: 'yes',
  credits_line: '',
  third_party_involved: false,
  third_party_note: '',
  has_samples: 'none',
  sample_evidence_url: '',
  is_cover: false,
  exclusivity_note: '',
  publishing_admin_note: '',
  territorial_note: '',
  confirm_accurate: false,
});

export default function SongUploadWizard({ token, userName, onSuccess, onCancel }) {
  const { lang } = useLang();
  const [step, setStep] = useState(0);
  const [taxRates, setTaxRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [generatingCover, setGeneratingCover] = useState(false);

  const [titleEs, setTitleEs] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [genre, setGenre] = useState('');
  const [duration, setDuration] = useState(0);
  const [isrc, setIsrc] = useState('');
  const [audioUrl, setAudioUrl] = useState('');

  const [coverSource, setCoverSource] = useState('upload');
  const [coverUrl, setCoverUrl] = useState('');
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiPreviewUrl, setAiPreviewUrl] = useState('');

  const [songLegal, setSongLegal] = useState(emptyLegal);
  const [signerName, setSignerName] = useState(userName || '');
  const [acceptSongTerms, setAcceptSongTerms] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [marketHint, setMarketHint] = useState(null);
  const [compliancePreview, setCompliancePreview] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API}/api/fiscal/tax-rates`)
      .then((r) => r.json())
      .then((d) => setTaxRates(d.tax_rates || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const code = songLegal.jurisdiction_country;
    if (!code) {
      setMarketHint(null);
      return;
    }
    const loc = lang === 'es' ? 'es' : 'en';
    fetch(`${API}/api/legal/market-hints/${code}?locale=${loc}`)
      .then((r) => r.json())
      .then((d) => (d.found ? setMarketHint(d) : setMarketHint(null)))
      .catch(() => setMarketHint(null));
  }, [songLegal.jurisdiction_country, lang]);

  const orderedCountryCodes = () => {
    const all = Object.keys(taxRates);
    const rest = all.filter((c) => !PRIORITY_MARKET_CODES.includes(c)).sort();
    return [...PRIORITY_MARKET_CODES.filter((c) => all.includes(c)), ...rest];
  };

  const onAudioFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingAudio(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`${API}/api/upload/audio`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.url) setAudioUrl(data.url);
    } finally {
      setUploadingAudio(false);
    }
  };

  const onCoverFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await fetch(`${API}/api/upload/image`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.url) {
        setCoverUrl(data.url);
        setUploadPreviewUrl(data.url);
      }
    } finally {
      setUploadingCover(false);
    }
  };

  const generateCoverAi = async () => {
    if (!aiPrompt.trim()) return;
    setGeneratingCover(true);
    try {
      const res = await fetch(`${API}/api/ai/generate-image`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt: `${aiPrompt}. Album cover art, no text, square composition, high quality.`,
          target: 'artist_cover',
          skip_profile_update: true,
          ephemeral: true,
        }),
      });
      const data = await res.json();
      if (data.url) setAiPreviewUrl(data.url);
    } catch {
      /* ignore */
    }
    setGeneratingCover(false);
  };

  const buildInvoicePreview = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/fiscal/tax-profile`, { headers });
      const d = await res.json();
      const tp = d.profile || {};
      const invRes = await fetch(`${API}/api/fiscal/preview-invoice`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          title: titleEs || 'Track',
          tax_country: tp.tax_country || 'DEFAULT',
        }),
      });
      if (invRes.ok) {
        const inv = await invRes.json();
        setInvoicePreview(inv);
      }
    } catch {
      setInvoicePreview(null);
    }
  }, [titleEs, token]);

  const fetchCompliancePreview = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/legal/preview-compliance`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ song_legal: songLegal, locale: lang === 'es' ? 'es' : 'en' }),
      });
      if (res.ok) {
        const data = await res.json();
        setCompliancePreview(data.legal_compliance || null);
      } else setCompliancePreview(null);
    } catch {
      setCompliancePreview(null);
    }
  }, [songLegal, token]);

  useEffect(() => {
    if (step === 4) {
      buildInvoicePreview();
      fetchCompliancePreview();
    }
  }, [step, buildInvoicePreview, fetchCompliancePreview]);

  const canNext = () => {
    if (step === 0) return titleEs.trim() && audioUrl;
    if (step === 1) {
      const hasUpload = uploadPreviewUrl;
      const hasAi = aiPreviewUrl;
      if (coverSource === 'upload') return hasUpload;
      return hasAi && aiPrompt.trim();
    }
    if (step === 2) {
      if (!songLegal.jurisdiction_country) return false;
      if (songLegal.has_samples === 'uncleared' && !songLegal.sample_evidence_url?.trim()) return false;
      return songLegal.confirm_accurate;
    }
    if (step === 3) return acceptSongTerms && signerName.trim();
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      const finalCover = coverSource === 'upload' ? coverUrl : aiPreviewUrl;
      const res = await fetch(`${API}/api/artists/me/songs`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          title_es: titleEs,
          title_en: titleEn,
          audio_url: audioUrl,
          cover: finalCover,
          genre,
          duration: parseInt(duration, 10) || 0,
          isrc,
          signer_name: signerName,
          accept_song_terms: true,
          cover_art: {
            source: coverSource,
            ai_prompt: aiPrompt,
            preview_note: lang === 'es' ? 'Vista previa aceptada por el artista' : 'Preview accepted by artist',
          },
          song_legal: songLegal,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      onSuccess?.(data);
    } catch (e) {
      alert(e.message || 'Error');
    }
    setLoading(false);
  };

  const steps = [
    lang === 'es' ? 'Audio y datos' : 'Audio & metadata',
    lang === 'es' ? 'Portada' : 'Cover art',
    lang === 'es' ? 'Derechos (obra)' : 'Rights (work)',
    lang === 'es' ? 'Firma' : 'Signature',
    lang === 'es' ? 'Resumen' : 'Summary',
  ];

  return (
    <div className="rounded-[28px] border border-white/10 bg-black/50 backdrop-blur-xl overflow-hidden">
      <div className="p-5 border-b border-white/10">
        <p className="text-[10px] uppercase tracking-[0.24em] text-pulse-muted mb-2">PulseMusic</p>
        <h3 className="font-heading text-xl font-bold">{lang === 'es' ? 'Subir canción' : 'Upload track'}</h3>
        <div className="flex gap-1 mt-4">
          {steps.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full ${step >= i ? 'bg-pulse-primary' : 'bg-white/10'}`} />
          ))}
        </div>
      </div>

      <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
        {step === 0 && (
          <div className="space-y-3">
            <input
              className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2 text-sm"
              placeholder="Título (ES)"
              value={titleEs}
              onChange={(e) => setTitleEs(e.target.value)}
            />
            <input
              className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2 text-sm"
              placeholder="Title (EN)"
              value={titleEn}
              onChange={(e) => setTitleEn(e.target.value)}
            />
            <input
              className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2 text-sm"
              placeholder={lang === 'es' ? 'Género' : 'Genre'}
              value={genre}
              onChange={(e) => setGenre(e.target.value)}
            />
            <input
              type="number"
              className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2 text-sm"
              placeholder={lang === 'es' ? 'Duración (seg)' : 'Duration (sec)'}
              value={duration || ''}
              onChange={(e) => setDuration(parseInt(e.target.value, 10) || 0)}
            />
            <input
              className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2 text-sm"
              placeholder="ISRC (opcional)"
              value={isrc}
              onChange={(e) => setIsrc(e.target.value)}
            />
            <label className="flex items-center gap-2 text-sm text-pulse-primary cursor-pointer">
              <Music size={16} />
              <span>{uploadingAudio ? '...' : lang === 'es' ? 'Subir audio' : 'Upload audio'}</span>
              <input type="file" accept="audio/*" className="hidden" onChange={onAudioFile} />
            </label>
            {audioUrl && <p className="text-xs text-green-400">{lang === 'es' ? 'Audio listo' : 'Audio OK'}</p>}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <p className="text-xs text-pulse-muted leading-relaxed">{lang === 'es' ? COVER_HINT_ES : COVER_HINT_EN}</p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCoverSource('upload')}
                className={`flex-1 rounded-xl py-2 text-sm font-semibold border ${coverSource === 'upload' ? 'border-pulse-primary bg-pulse-primary/15 text-pulse-primary' : 'border-white/10'}`}
              >
                {lang === 'es' ? 'Subir imagen' : 'Upload image'}
              </button>
              <button
                type="button"
                onClick={() => setCoverSource('ai')}
                className={`flex-1 rounded-xl py-2 text-sm font-semibold border flex items-center justify-center gap-1 ${coverSource === 'ai' ? 'border-pulse-primary bg-pulse-primary/15 text-pulse-primary' : 'border-white/10'}`}
              >
                <Sparkles size={14} /> IA (DALL·E)
              </button>
            </div>

            {coverSource === 'upload' && (
              <div>
                <label className="text-xs text-cyan-300 cursor-pointer inline-flex items-center gap-2">
                  <ImageIcon size={14} /> {lang === 'es' ? 'Elegir archivo' : 'Choose file'}
                  <input type="file" accept="image/*" className="hidden" onChange={onCoverFile} />
                </label>
                {uploadPreviewUrl && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] text-pulse-muted mb-1">1:1</p>
                      <div className="aspect-square w-full max-w-[140px] rounded-xl overflow-hidden border border-white/10 bg-black">
                        <img src={uploadPreviewUrl} alt="" className="w-full h-full object-cover" />
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-pulse-muted mb-1">16:9</p>
                      <div className="aspect-video w-full max-w-[180px] rounded-xl overflow-hidden border border-white/10 bg-black">
                        <img src={uploadPreviewUrl} alt="" className="w-full h-full object-cover" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {coverSource === 'ai' && (
              <div className="space-y-2">
                <textarea
                  className="w-full min-h-[80px] bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2 text-sm"
                  placeholder={lang === 'es' ? 'Describe la portada (estética, colores, atmósfera)' : 'Describe the cover (aesthetic, colors, mood)'}
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                />
                <button
                  type="button"
                  onClick={generateCoverAi}
                  disabled={generatingCover || !aiPrompt.trim()}
                  className="rounded-full bg-pulse-primary text-black px-4 py-2 text-xs font-bold disabled:opacity-50"
                >
                  {generatingCover ? '...' : lang === 'es' ? 'Generar vista previa' : 'Generate preview'}
                </button>
                {aiPreviewUrl && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] text-pulse-muted mb-1">1:1</p>
                      <div className="aspect-square w-full max-w-[140px] rounded-xl overflow-hidden border border-white/10 bg-black">
                        <img src={aiPreviewUrl} alt="" className="w-full h-full object-cover" />
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-pulse-muted mb-1">16:9</p>
                      <div className="aspect-video w-full max-w-[180px] rounded-xl overflow-hidden border border-white/10 bg-black">
                        <img src={aiPreviewUrl} alt="" className="w-full h-full object-cover" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3 text-sm">
            <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-3 text-xs text-amber-100/95 leading-relaxed">
              <p className="font-semibold text-amber-200 mb-1">
                {lang === 'es' ? 'Importante (no es asesoramiento legal)' : 'Important (not legal advice)'}
              </p>
              <p>
                {lang === 'es'
                  ? 'PulseMusic no valida automáticamente licencias mecánicas, sincronización ni acuerdos con terceros. Lo que declares es tu responsabilidad. Si marcas cover, samples sin licencia completa o terceros con derechos, la revisión humana puede ser obligatoria antes de la máxima visibilidad.'
                  : 'PulseMusic does not auto-clear mechanical or sync licences or third-party agreements. Your declarations are your responsibility. Covers, uncleared samples, or third-party rights may require human review before broad visibility.'}
              </p>
            </div>
            <label className="block">
              <span className="text-xs text-pulse-muted">{lang === 'es' ? 'País de referencia (obra / residencia artística)' : 'Reference country (work)'}</span>
              <select
                className="mt-1 w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2"
                value={songLegal.jurisdiction_country}
                onChange={(e) => setSongLegal((s) => ({ ...s, jurisdiction_country: e.target.value }))}
              >
                <option value="">{lang === 'es' ? 'Selecciona…' : 'Select…'}</option>
                <option disabled className="text-pulse-muted">
                  — {lang === 'es' ? 'Mercados prioritarios (orientación)' : 'Priority markets (guidance)'} —
                </option>
                {orderedCountryCodes().map((code) => (
                  <option key={code} value={code}>
                    {PRIORITY_MARKET_CODES.includes(code) ? '★ ' : ''}
                    {taxRates[code]?.name || code}
                  </option>
                ))}
              </select>
            </label>
            {marketHint?.hint && (
              <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3 text-xs text-cyan-50/95">
                <p className="text-[10px] uppercase tracking-wider text-cyan-300/90 mb-1">{marketHint.label}</p>
                <p className="leading-relaxed">{marketHint.hint}</p>
                {marketHint.collecting_note && <p className="mt-2 text-cyan-200/80">{marketHint.collecting_note}</p>}
                <p className="mt-2 text-[10px] text-pulse-muted">{marketHint.disclaimer}</p>
              </div>
            )}
            <label className="block text-xs">
              {lang === 'es' ? 'Derechos sobre master' : 'Master rights'}
              <select
                className="mt-1 w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2"
                value={songLegal.master_rights}
                onChange={(e) => setSongLegal((s) => ({ ...s, master_rights: e.target.value }))}
              >
                <option value="self">{lang === 'es' ? 'Soy titular' : 'I own'}</option>
                <option value="shared">{lang === 'es' ? 'Co-titular' : 'Co-owner'}</option>
                <option value="licensed">{lang === 'es' ? 'Licencia válida' : 'Licensed'}</option>
              </select>
            </label>
            <label className="block text-xs">
              {lang === 'es' ? 'Composición / edición' : 'Publishing'}
              <select
                className="mt-1 w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2"
                value={songLegal.publishing_rights}
                onChange={(e) => setSongLegal((s) => ({ ...s, publishing_rights: e.target.value }))}
              >
                <option value="yes">{lang === 'es' ? 'Control / licencia' : 'Control / license'}</option>
                <option value="partial">{lang === 'es' ? 'Parcial' : 'Partial'}</option>
                <option value="no">{lang === 'es' ? 'Tercero' : 'Third party'}</option>
              </select>
            </label>
            <input
              className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2 text-xs"
              placeholder={lang === 'es' ? 'Créditos (autor, productor…)' : 'Credits'}
              value={songLegal.credits_line}
              onChange={(e) => setSongLegal((s) => ({ ...s, credits_line: e.target.value }))}
            />
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={songLegal.third_party_involved}
                onChange={(e) => setSongLegal((s) => ({ ...s, third_party_involved: e.target.checked }))}
              />
              {lang === 'es' ? 'Hay terceros con derechos' : 'Third parties involved'}
            </label>
            {songLegal.third_party_involved && (
              <input
                className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2 text-xs"
                value={songLegal.third_party_note}
                onChange={(e) => setSongLegal((s) => ({ ...s, third_party_note: e.target.value }))}
                placeholder="..."
              />
            )}
            <label className="block text-xs">
              {lang === 'es' ? 'Samples' : 'Samples'}
              <select
                className="mt-1 w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2"
                value={songLegal.has_samples}
                onChange={(e) => setSongLegal((s) => ({ ...s, has_samples: e.target.value }))}
              >
                <option value="none">{lang === 'es' ? 'Sin samples' : 'No samples'}</option>
                <option value="cleared">{lang === 'es' ? 'Samples liberados' : 'Cleared samples'}</option>
                <option value="uncleared">{lang === 'es' ? 'Samples no liberados (evidencia obligatoria)' : 'Uncleared (evidence required)'}</option>
              </select>
            </label>
            {songLegal.has_samples === 'uncleared' && (
              <input
                className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2 text-xs"
                placeholder="URL de evidencia / licencia"
                value={songLegal.sample_evidence_url}
                onChange={(e) => setSongLegal((s) => ({ ...s, sample_evidence_url: e.target.value }))}
              />
            )}
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={songLegal.is_cover} onChange={(e) => setSongLegal((s) => ({ ...s, is_cover: e.target.checked }))} />
              {lang === 'es' ? 'Es un cover' : 'This is a cover'}
            </label>
            <input
              className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2 text-xs"
              placeholder={lang === 'es' ? 'Exclusividades / distribuidora (opc.)' : 'Exclusivity / label (opt.)'}
              value={songLegal.exclusivity_note}
              onChange={(e) => setSongLegal((s) => ({ ...s, exclusivity_note: e.target.value }))}
            />
            <label className="flex items-start gap-2 text-xs text-pulse-muted">
              <input
                type="checkbox"
                checked={songLegal.confirm_accurate}
                onChange={(e) => setSongLegal((s) => ({ ...s, confirm_accurate: e.target.checked }))}
                className="mt-0.5"
              />
              {lang === 'es'
                ? 'Confirmo que la información es veraz y tengo derecho a publicar esta obra en PulseMusic.'
                : 'I confirm this information is accurate and I have the right to publish this work.'}
            </label>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3 text-xs text-cyan-100">
              {lang === 'es'
                ? 'Al continuar aceptas la declaración de derechos de subida vigente (song_upload_rights).'
                : 'You accept the current song upload rights declaration.'}
            </div>
            <input
              className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2 text-sm"
              placeholder={lang === 'es' ? 'Nombre legal del firmante' : 'Legal name'}
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
            />
            <label className="flex items-start gap-2 text-xs">
              <input type="checkbox" checked={acceptSongTerms} onChange={(e) => setAcceptSongTerms(e.target.checked)} className="mt-0.5" />
              {lang === 'es' ? 'Acepto la declaración de derechos para esta canción.' : 'I accept the rights declaration for this track.'}
            </label>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3 text-xs">
            {compliancePreview?.requires_manual_review && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-amber-100">
                <p className="font-semibold text-amber-200 mb-1">
                  {lang === 'es' ? 'Revisión humana probable' : 'Human review likely'}
                </p>
                <p className="leading-relaxed">
                  {lang === 'es'
                    ? 'Según tu declaración, el sistema marcará esta pista para revisión antes de aprobación amplia (p. ej. cover, samples o terceros con derechos). La revisión puede tardar hasta ~12 h en horario laborable.'
                    : 'Based on your declaration, this track may be queued for review before broad approval (e.g. cover, samples, or third-party rights). Review may take up to ~12 business hours.'}
                </p>
                {compliancePreview.reasons?.length > 0 && (
                  <ul className="mt-2 list-disc list-inside text-amber-100/90">
                    {compliancePreview.reasons.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <p className="text-pulse-muted">{lang === 'es' ? 'Vista previa orientativa (no factura fiscal)' : 'Illustrative preview (not a tax invoice)'}</p>
            {invoicePreview?.invoice ? (
              <div className="rounded-xl border border-white/10 p-3 space-y-1 bg-white/[0.03]">
                <p className="font-heading text-sm text-white">{invoicePreview.invoice.type_label}</p>
                <p className="text-pulse-muted">{invoicePreview.invoice.invoice_number}</p>
                <p>{invoicePreview.invoice.recipient?.name}</p>
                <p className="text-pulse-muted">{lang === 'es' ? 'País fiscal' : 'Tax country'}: {invoicePreview.invoice.tax_country}</p>
                <p className="text-[10px] text-amber-200/90 mt-2">{invoicePreview.invoice.disclaimer}</p>
              </div>
            ) : (
              <p className="text-pulse-muted">—</p>
            )}
          </div>
        )}
      </div>

      <div className="p-5 border-t border-white/10 flex justify-between gap-2">
        <button type="button" onClick={() => (step === 0 ? onCancel?.() : setStep((s) => s - 1))} className="rounded-full border border-white/15 px-4 py-2 text-sm">
          <ChevronLeft size={16} className="inline" /> {lang === 'es' ? 'Atrás' : 'Back'}
        </button>
        {step < 4 ? (
          <button
            type="button"
            disabled={!canNext()}
            onClick={() => setStep((s) => s + 1)}
            className="rounded-full bg-pulse-primary text-black px-5 py-2 text-sm font-bold disabled:opacity-40"
          >
            {lang === 'es' ? 'Siguiente' : 'Next'} <ChevronRight size={16} className="inline" />
          </button>
        ) : (
          <button
            type="button"
            disabled={loading}
            onClick={submit}
            className="rounded-full bg-pulse-primary text-black px-5 py-2 text-sm font-bold inline-flex items-center gap-1"
          >
            <Check size={16} /> {loading ? '...' : lang === 'es' ? 'Publicar' : 'Publish'}
          </button>
        )}
      </div>
    </div>
  );
}
